import React from 'react';

import {
  FormDropdown,
  FormFieldStack,
  FormInput,
  FormYesNoChoice,
  NumberInput,
  SubsectionForm,
  formFieldInStack,
} from '../../../../components/forms';
import { SelectedFile } from '../../../../components/forms/FileUpload';
import { requestStepFieldModeProps } from '../../../../components/forms/viewOnlyFieldUtils';
import { PermissionAwareField } from '../../../../components/PermissionAwareField';
import { FACILITY_POWER_LARGE_UPS_SUBTITLE } from '../../../../constants/facilityPowerSectionSubtitles';
import { FACILITY_POWER_LARGE_UPS_SUBSECTION_FIELDS } from '../../../../constants/requestFieldDescriptors';
import { SECTION_IDS } from '../../../../constants/sectionIds';
import { useFieldLabels } from '../../../../hooks/useFieldLabels';
import { useFormFieldVisibilityContext } from '../../../../hooks/useFormFieldVisibilityContext';
import { SavedAttachment } from '../../../../services/attachmentService';
import { Request } from '../../../../types';
import { subsectionHeaderMetricsProps } from '../../../../utils/subsectionHeaderMetrics';

type LargeUpsAttachmentBundle = {
  attachmentsBySection: Record<string, SavedAttachment[]>;
  onAttachFiles?: (sectionId: string) => void;
  onDeleteAttachment?: (attachmentId: number) => void;
  selectedFiles: Record<string, SelectedFile[]>;
  onFilesSelected?: (sectionId: string, files: SelectedFile[]) => void;
  offlineMode: boolean;
  onCaptionChange?: (attachmentId: number, caption: string) => void;
  pendingCaptionEdits?: Record<number, string>;
};

export type LargeUpsInformationProps = {
  sectionTitle?: string;
} & (
  | ({
      mode: 'initial';
      formData: Record<string, unknown>;
      updateFormData: (
        field: string,
        value: string | number | boolean | undefined,
      ) => void;
      requestId?: string;
      disabled: boolean;
      isEditing: boolean;
      showFullDetails?: boolean;
    } & LargeUpsAttachmentBundle)
  | ({
      mode: 'persisted';
      request: Request;
      isEditing: boolean;
      editedData?: Record<string, unknown>;
      showFullDetails?: boolean;
      getValue: (field: string) => string | number | boolean;
      updateEditedData: (
        field: string,
        value: string | number | boolean | undefined,
      ) => void;
    } & LargeUpsAttachmentBundle)
);

const redundancyOptions = ['N', 'N + 1', '2N'];
const batteryChemistryOptions = ['LIB', 'VRLA', 'Pure Lead'];

// #997 QA-A2: powerSourceLocation + powerFireSuppressionNeeds are no longer rendered
// on this Facility Power readiness card (they moved to the per-unit Large UPS editor),
// so exclude them from this card's completeness badge denominator/numerator.
const FACILITY_POWER_LARGE_UPS_METRICS_FIELDS =
  FACILITY_POWER_LARGE_UPS_SUBSECTION_FIELDS.filter(
    field =>
      field.key !== 'powerSourceLocation' &&
      field.key !== 'powerFireSuppressionNeeds',
  );

/**
 * Main “Large UPS” card (readiness fields + attachments) for create and opened-request layouts.
 */
export const LargeUpsInformation: React.FC<
  LargeUpsInformationProps
> = props => {
  const {
    sectionTitle = 'Large UPS',
    attachmentsBySection,
    onAttachFiles,
    onDeleteAttachment,
    selectedFiles,
    onFilesSelected,
    offlineMode,
    onCaptionChange,
    pendingCaptionEdits,
  } = props;

  const { getGeneralLabel } = useFieldLabels();

  const showFullDetails =
    props.mode === 'initial'
      ? props.showFullDetails ?? false
      : props.showFullDetails ?? true;
  const requestStageForVisibility =
    props.mode === 'initial'
      ? String(
          (props.formData as { sysRequestStage?: string; stage?: string })
            .sysRequestStage ??
            (props.formData as { stage?: string }).stage ??
            '',
        )
      : String(props.request.sysRequestStage ?? props.request.stage ?? '');
  const requestIdForVisibility =
    props.mode === 'initial' ? props.requestId : props.request.id;
  const fieldVisibility = useFormFieldVisibilityContext({
    showFullDetails,
    requestStage: requestStageForVisibility,
    requestId: requestIdForVisibility,
  });

  if (props.mode === 'initial') {
    const { formData, updateFormData, requestId, disabled, isEditing } = props;
    const fd = formData as Record<string, unknown>;
    const initStr = (key: string): string => {
      const raw = fd[key];
      if (raw === undefined || raw === null) {
        return '';
      }
      return String(raw);
    };
    const requestStageStr = requestStageForVisibility;
    const largeUpsMetrics = subsectionHeaderMetricsProps(
      FACILITY_POWER_LARGE_UPS_METRICS_FIELDS,
      fd,
      fieldVisibility,
    );

    return (
      <SubsectionForm
        title={sectionTitle}
        {...largeUpsMetrics}
        titleSubtitle={FACILITY_POWER_LARGE_UPS_SUBTITLE}
        sectionId={SECTION_IDS.FACILITY_POWER_LARGE_UPS}
        requestId={requestId}
        allowAttachments={true}
        attachments={
          attachmentsBySection[SECTION_IDS.FACILITY_POWER_LARGE_UPS] || []
        }
        onAttachFiles={() =>
          onAttachFiles?.(SECTION_IDS.FACILITY_POWER_LARGE_UPS)
        }
        onDeleteAttachment={onDeleteAttachment}
        isEditing={isEditing || !disabled}
        disabled={disabled}
        selectedFiles={
          selectedFiles[SECTION_IDS.FACILITY_POWER_LARGE_UPS] || []
        }
        onFilesSelected={
          onFilesSelected
            ? files =>
                onFilesSelected(SECTION_IDS.FACILITY_POWER_LARGE_UPS, files)
            : undefined
        }
        isOffline={offlineMode}
      >
        <FormFieldStack>
          <PermissionAwareField
            fieldId="powerExistingSiteProtectedByLargeKwUps"
            requestStage={requestStageStr}
            requestId={requestId}
            showFullDetails={showFullDetails}
          >
            <FormYesNoChoice
              label="Existing site protected by large kW UPS"
              value={initStr('powerExistingSiteProtectedByLargeKwUps')}
              onSelect={value =>
                updateFormData(
                  'powerExistingSiteProtectedByLargeKwUps',
                  String(value),
                )
              }
              disabled={disabled}
              containerStyle={formFieldInStack}
            />
          </PermissionAwareField>
          {showFullDetails ? (
            <>
              <PermissionAwareField
                fieldId="powerModelOfExistingUps"
                requestStage={requestStageStr}
                requestId={requestId}
                showFullDetails={showFullDetails}
              >
                <FormInput
                  label="If yes, model of existing UPS"
                  value={initStr('powerModelOfExistingUps')}
                  onChangeText={value =>
                    updateFormData('powerModelOfExistingUps', value)
                  }
                  placeholder="Enter existing UPS model information"
                  editable={
                    initStr('powerExistingSiteProtectedByLargeKwUps') === 'Yes'
                  }
                  disabled={disabled}
                />
              </PermissionAwareField>
              <PermissionAwareField
                fieldId="powerLargeUpsDescription"
                requestStage={requestStageStr}
                requestId={requestId}
                showFullDetails={showFullDetails}
              >
                <FormInput
                  label="Description"
                  value={initStr('powerLargeUpsDescription')}
                  onChangeText={value =>
                    updateFormData('powerLargeUpsDescription', value)
                  }
                  placeholder="Enter description"
                  multiline
                  maxLength={10000}
                  richText
                  requestId={requestId}
                  requestSection={SECTION_IDS.FACILITY_POWER_LARGE_UPS}
                  isOnline={!offlineMode}
                  disabled={disabled}
                />
              </PermissionAwareField>
              <PermissionAwareField
                fieldId="powerLevelOfRedundancy"
                requestStage={requestStageStr}
                requestId={requestId}
                showFullDetails={showFullDetails}
              >
                <FormDropdown
                  label="Level of Redundancy"
                  value={initStr('powerLevelOfRedundancy')}
                  options={redundancyOptions}
                  onSelect={value =>
                    updateFormData('powerLevelOfRedundancy', String(value))
                  }
                  placeholder="Select redundancy level"
                  disabled={disabled}
                />
              </PermissionAwareField>
              <PermissionAwareField
                fieldId="powerRequiredRedundancyForUps"
                requestStage={requestStageStr}
                requestId={requestId}
                showFullDetails={showFullDetails}
              >
                <FormDropdown
                  label="Required redundancy for UPS system"
                  value={initStr('powerRequiredRedundancyForUps')}
                  options={redundancyOptions}
                  onSelect={value =>
                    updateFormData(
                      'powerRequiredRedundancyForUps',
                      String(value),
                    )
                  }
                  placeholder="Select required redundancy"
                  disabled={disabled}
                />
              </PermissionAwareField>
              <PermissionAwareField
                fieldId="powerBatteryChemistry"
                requestStage={requestStageStr}
                requestId={requestId}
                showFullDetails={showFullDetails}
              >
                <FormDropdown
                  label="Battery chemistry"
                  value={initStr('powerBatteryChemistry')}
                  options={batteryChemistryOptions}
                  onSelect={value =>
                    updateFormData('powerBatteryChemistry', String(value))
                  }
                  placeholder="Select battery chemistry"
                  disabled={disabled}
                />
              </PermissionAwareField>
              <PermissionAwareField
                fieldId="powerBatteryRuntimeRequiredMin"
                requestStage={requestStageStr}
                requestId={requestId}
                showFullDetails={showFullDetails}
              >
                <NumberInput
                  label={getGeneralLabel('Battery Runtime Required')}
                  value={initStr('powerBatteryRuntimeRequiredMin')}
                  onChangeText={value =>
                    updateFormData('powerBatteryRuntimeRequiredMin', value)
                  }
                  placeholder="Enter required battery runtime in minutes"
                  min={0}
                  showStepper={false}
                  disabled={disabled}
                />
              </PermissionAwareField>
              {/* #997 QA-A2: powerSourceLocation + powerFireSuppressionNeeds moved
                  to the per-unit Large UPS editor (single writer); removed here. */}
            </>
          ) : null}
        </FormFieldStack>
      </SubsectionForm>
    );
  }

  const { request, isEditing, editedData, getValue, updateEditedData } = props;
  const { disabled: fieldDisabled, readOnlyMode } =
    requestStepFieldModeProps(isEditing);
  const permissionStage = String(
    request.stage ?? request.sysRequestStage ?? '',
  );
  const headerValues = {
    ...(request as unknown as Record<string, unknown>),
    ...(isEditing ? editedData ?? {} : {}),
  };
  const largeUpsMetrics = subsectionHeaderMetricsProps(
    FACILITY_POWER_LARGE_UPS_METRICS_FIELDS,
    headerValues,
    fieldVisibility,
  );

  return (
    <SubsectionForm
      title={sectionTitle}
      {...largeUpsMetrics}
      titleSubtitle={FACILITY_POWER_LARGE_UPS_SUBTITLE}
      sectionId={SECTION_IDS.FACILITY_POWER_LARGE_UPS}
      requestId={request.id}
      allowAttachments={true}
      attachments={
        attachmentsBySection[SECTION_IDS.FACILITY_POWER_LARGE_UPS] || []
      }
      onAttachFiles={() =>
        onAttachFiles?.(SECTION_IDS.FACILITY_POWER_LARGE_UPS)
      }
      onDeleteAttachment={onDeleteAttachment}
      isEditing={isEditing}
      disabled={fieldDisabled}
      readOnlyMode={readOnlyMode}
      selectedFiles={selectedFiles[SECTION_IDS.FACILITY_POWER_LARGE_UPS] || []}
      onFilesSelected={
        onFilesSelected
          ? files =>
              onFilesSelected(SECTION_IDS.FACILITY_POWER_LARGE_UPS, files)
          : undefined
      }
      isOffline={offlineMode}
      onCaptionChange={onCaptionChange}
      pendingCaptionEdits={pendingCaptionEdits}
    >
      <FormFieldStack>
        <PermissionAwareField
          fieldId="powerExistingSiteProtectedByLargeKwUps"
          requestStage={permissionStage}
          requestId={request.id}
          showFullDetails={showFullDetails}
        >
          <FormYesNoChoice
            label="Existing site protected by large kW UPS"
            value={String(
              getValue('powerExistingSiteProtectedByLargeKwUps') ?? '',
            )}
            onSelect={
              isEditing
                ? value =>
                    updateEditedData(
                      'powerExistingSiteProtectedByLargeKwUps',
                      String(value),
                    )
                : () => {}
            }
            disabled={fieldDisabled}
            readOnlyMode={readOnlyMode}
            containerStyle={formFieldInStack}
          />
        </PermissionAwareField>
        {showFullDetails && (
          <>
            <PermissionAwareField
              fieldId="powerModelOfExistingUps"
              requestStage={permissionStage}
              requestId={request.id}
              showFullDetails={showFullDetails}
            >
              <FormInput
                label="If yes, model of existing UPS"
                value={getValue('powerModelOfExistingUps')}
                onChangeText={
                  isEditing
                    ? value =>
                        updateEditedData('powerModelOfExistingUps', value)
                    : () => {}
                }
                placeholder="Enter existing UPS model information"
                editable={
                  isEditing &&
                  getValue('powerExistingSiteProtectedByLargeKwUps') === 'Yes'
                }
                disabled={fieldDisabled}
                readOnlyMode={readOnlyMode}
              />
            </PermissionAwareField>
            <PermissionAwareField
              fieldId="powerLargeUpsDescription"
              requestStage={permissionStage}
              requestId={request.id}
              showFullDetails={showFullDetails}
            >
              <FormInput
                label="Description"
                value={getValue('powerLargeUpsDescription')}
                onChangeText={
                  isEditing
                    ? value =>
                        updateEditedData('powerLargeUpsDescription', value)
                    : () => {}
                }
                placeholder="Enter description"
                multiline
                maxLength={10000}
                richText
                requestId={String(request.id)}
                requestSection={SECTION_IDS.FACILITY_POWER_LARGE_UPS}
                isOnline={!offlineMode}
                disabled={fieldDisabled}
                readOnlyMode={readOnlyMode}
              />
            </PermissionAwareField>
            <PermissionAwareField
              fieldId="powerLevelOfRedundancy"
              requestStage={permissionStage}
              requestId={request.id}
              showFullDetails={showFullDetails}
            >
              <FormDropdown
                label="Level of Redundancy"
                value={getValue('powerLevelOfRedundancy')}
                options={redundancyOptions}
                onSelect={
                  isEditing
                    ? value =>
                        updateEditedData(
                          'powerLevelOfRedundancy',
                          String(value),
                        )
                    : () => {}
                }
                placeholder="Select redundancy level"
                disabled={fieldDisabled}
                readOnlyMode={readOnlyMode}
              />
            </PermissionAwareField>
            <PermissionAwareField
              fieldId="powerRequiredRedundancyForUps"
              requestStage={permissionStage}
              requestId={request.id}
              showFullDetails={showFullDetails}
            >
              <FormDropdown
                label="Required redundancy for UPS system"
                value={getValue('powerRequiredRedundancyForUps')}
                options={redundancyOptions}
                onSelect={
                  isEditing
                    ? value =>
                        updateEditedData(
                          'powerRequiredRedundancyForUps',
                          String(value),
                        )
                    : () => {}
                }
                placeholder="Select required redundancy"
                disabled={fieldDisabled}
                readOnlyMode={readOnlyMode}
              />
            </PermissionAwareField>
            <PermissionAwareField
              fieldId="powerBatteryChemistry"
              requestStage={permissionStage}
              requestId={request.id}
              showFullDetails={showFullDetails}
            >
              <FormDropdown
                label="Battery chemistry"
                value={getValue('powerBatteryChemistry')}
                options={batteryChemistryOptions}
                onSelect={
                  isEditing
                    ? value =>
                        updateEditedData('powerBatteryChemistry', String(value))
                    : () => {}
                }
                placeholder="Select battery chemistry"
                disabled={fieldDisabled}
                readOnlyMode={readOnlyMode}
              />
            </PermissionAwareField>
            <PermissionAwareField
              fieldId="powerBatteryRuntimeRequiredMin"
              requestStage={permissionStage}
              requestId={request.id}
              showFullDetails={showFullDetails}
            >
              <NumberInput
                label={getGeneralLabel('Battery Runtime Required')}
                value={getValue('powerBatteryRuntimeRequiredMin')}
                onChangeText={
                  isEditing
                    ? value =>
                        updateEditedData(
                          'powerBatteryRuntimeRequiredMin',
                          value,
                        )
                    : () => {}
                }
                placeholder="Enter required battery runtime in minutes"
                min={0}
                showStepper={false}
                disabled={fieldDisabled}
                readOnlyMode={readOnlyMode}
              />
            </PermissionAwareField>
            {/* #997 QA-A2: powerSourceLocation + powerFireSuppressionNeeds moved
                to the per-unit Large UPS editor (single writer); removed here. */}
          </>
        )}
      </FormFieldStack>
    </SubsectionForm>
  );
};

LargeUpsInformation.displayName = 'LargeUpsInformation';
